import { CurrencyPipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  AccountTransferData,
  AppBarAction,
  AppBarActionsService,
  AppBarTitleService,
  AppPageService,
  ButtonColor,
  CommonActionKey,
  MessageService,
  PromotionInfoData,
  SimpleDialogComponent,
  SimpleDialogData,
} from "common";
import { EMPTY, Subscription } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, finalize, tap } from "rxjs/operators";
import { BankAccountService } from "../../../bank-account/bank-account.service";
import { BankingService } from "../../../bank-account/banking.service";
import { LoanFacade } from "../../../loan/domain/+state/loan.facade";
import { ProductCode } from "../../../loan/domain/models/loan.model";
import {
  AccountTransferSubmittedDialogComponent
} from "../account-transfer-submitted-dialog/account-transfer-submitted-dialog.component";
import { AccountTransferComponentData } from "../account-transfer.model";

@UntilDestroy()
@Component({
    selector: "ifp-account-transfer-draw",
    templateUrl: "./account-transfer-draw.component.html",
    styleUrls: ["./account-transfer-draw.component.scss"],
    standalone: false
})
export class AccountTransferDrawComponent implements OnInit {
  form: UntypedFormGroup;
  data: AccountTransferComponentData;
  loanId: number;
  bankIntegrationRequired: boolean;
  promotionInfo: PromotionInfoData;
  submitSub: Subscription;
  accountTransferData: AccountTransferData;
  isCalculating: boolean;

  protected isLcf: boolean;
  protected continueDisabled: boolean;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly dialog: MatDialog,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly bankingService: BankingService,
    private readonly bankAccountService: BankAccountService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly loanFacade: LoanFacade,
    private readonly _appBarActionsService: AppBarActionsService,
    private readonly _currencyPipe: CurrencyPipe,
    private readonly appPageService: AppPageService
  ) {
  }

  ngOnInit(): void {
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: { accountTransfer: AccountTransferComponentData }) => {
        this.data = data.accountTransfer;
        this.formInit();
        this.accountTransferData = this.createAccountTransferData();
      });

    this.appBarTitleService.title = "Make a draw";

    this.form.controls.drawAmount.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(() => {
          this.isCalculating = true;
          this.enableSaveAction();
        }),
        debounceTime(500)
      )
      .subscribe((value: string) => {
        this.data.accountTransfer.amountInput = value;
        this.accountNewBalancesInit();
      });

    this.form.controls.note.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((value: string) => {
        this.data.accountTransfer.note = value;
      });

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.enableSaveAction());

    this._appBarActionsService.invoking
      .pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));

    this.bankAccountService
      .getIsBankLinkReminderRequired(this.data.loan.entityId)
      .subscribe(
        (isBankLinkReminderRequired: boolean) =>
          (this.bankIntegrationRequired = isBankLinkReminderRequired && this.data.loan?.productCode !== ProductCode.Law)
      );
  }

  cancel(): void {
    const result = this.appPageService.backToIfPossible();
    if (result) {
      return;
    }

    if (this.data.redirectedFromAgreement) {
      this.router.navigate(["dashboard"]);
    }

    this.appPageService.back();
  }

  applyPromotionChanged(promotion: PromotionInfoData): void {
    this.promotionInfo = promotion;
    if (this.data.accountTransfer.amountInput)
      this.accountNewBalancesInit();
  }

  setAmount(value: number): void {
    this.form.controls.drawAmount.setValue(value);
  }

  save(): void {
    if (this.isCalculating) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    SimpleDialogComponent.show(this.dialog, this.getSaveConfirmationDialogData()).subscribe((actionKey: string) => {
      if(actionKey === CommonActionKey.Confirm) {
        this.data.accountTransfer.submit().subscribe({
          next: () => this.submitSuccess(),
          error: (err) => this.submitError(err),
        });
      }
    });
  }

  private getSaveConfirmationDialogData() : SimpleDialogData {
    return {
      title: "Confirm draw?",
      body: `You are making a draw in the amount of ${this._currencyPipe.transform(this.data.accountTransfer.amountInput)}.`,
      actions: [
        { label: "CANCEL", buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
        { label: "CONFIRM", buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
      ],
    }
  }

  private setContinueDisabled(): void {
    this.continueDisabled =
      (this.isLcf && this.form.controls.lcfAcknowledgement.value !== true);
  }

  private formInit(): void {
    this.form = this.formBuilder.group({
      drawAmount: [
        undefined,
        [
          Validators.required,
          Validators.min(this.data.accountTransfer.amountMinimum),
          Validators.max(this.data.accountTransfer.amountMaximum),
        ],
      ],
      note: undefined,
      lcfAcknowledgement: [""],
    });

    this.loanId = this.data.loan.id;
    this.isLcf = this.data?.loan?.productCode == ProductCode.Law;
    this.setContinueDisabled();

    this.form.controls.lcfAcknowledgement.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.setContinueDisabled())
    this.setAppBarActions();
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void =
      this[action.id].bind(this);
    actionHandler(action);
  }

  private enableSaveAction(): void {
    const canSave = this.form.valid && !this.continueDisabled && !this.isCalculating;
    this._appBarActionsService.enable("save", canSave);
  }

  private submitError(error): void {
    this.messageService.error(error);
    this.submitSubClear();
  }

  private submitSubClear(): void {
    if (this.submitSub) this.submitSub.unsubscribe();
    this.submitSub = null;
  }

  private submitSuccess(): void {
    const title = "Draw request submitted";
    let closeButtonLabel: string;
    if (this.bankIntegrationRequired) {
      closeButtonLabel = "Link your bank";
      this.bankingService.integrationReminded(this.data.loan.entityId);
    }

    this.loanFacade.refreshLoan(this.loanId).subscribe();

    AccountTransferSubmittedDialogComponent.show(
      this.dialog,
      this.data.accountTransfer,
      title,
      "draw",
      this.bankIntegrationRequired,
      closeButtonLabel
    )
      .subscribe(() => {
        if (this.bankIntegrationRequired && !this.appPageService.isBackAvailable())
          this.router.navigate(["/bank-link-reminder"]);
        else
          this.cancel();
      });
  }

  private accountNewBalancesInit(): void {
    const value = this.data.accountTransfer.amountInput
      ? parseFloat(this.data.accountTransfer.amountInput)
      : 0;

    this.loanFacade
      .drawPreview(this.data.loan.id, value, this.promotionInfo?.promoCode)
      .pipe(
        catchError((err) => {
          this.messageService.error(err);
          return EMPTY;
        }),
        finalize(() => {
          this.isCalculating = false;
          this.enableSaveAction();
        }),
      )
      .subscribe((res) => {
        this.data.accountTransfer.balanceAfter.outstandingBalance = res.newOutstandingBalance;
        this.data.accountTransfer.balanceAfter.availableBalance = res.newAvailableFunds;
        this.data.accountTransfer.balanceAfter.upcomingPaymentAmount = res.newUpcomingPaymentAmount;
        this.data.accountTransfer.balanceAfter.upcomingPaymentDate = res.newUpcomingPaymentDate;
        this.data.accountTransfer.fee = res.fee;
        this.data.accountTransfer.thirdPartyPayoffAmount = res.thirdPartyPayoffAmount;
        this.data.accountTransfer.disbursedAmount = res.disbursedAmount;
        this.data.accountTransfer.promotionInfo = res.promotionInfo;
        this.accountTransferData = this.createAccountTransferData();
      });
  }

  private setAppBarActions(): void {
    this._appBarActionsService.actions = [];
    this._appBarActionsService.actions.push({
      id: "cancel",
      label: "Cancel",
      buttonType: "button",
    });
    this._appBarActionsService.actions.push({
      id: "save",
      label: "Continue",
      disabled: !this.form.dirty,
      buttonType: "submit",
      buttonAppearance: "flat",
      buttonColor: "primary",
    });
  }

  private createAccountTransferData(): AccountTransferData {
    return {
      loan: {
        id: this.data.loan.id,
        description: this.data.loan.description,
        paymentAmount: this.data.accountTransfer.loanInfo?.paymentAmount
      },
      balanceAfter: this.data.accountTransfer.balanceAfter,
      promotionInfo: this.data.accountTransfer.promotionInfo,
      from: this.data.accountTransfer.from,
      to: this.data.accountTransfer.to,
      fee: this.data.accountTransfer.fee,
      disbursedAmount: this.data.accountTransfer.disbursedAmount,
      thirdPartyPayoffAmount: this.data.accountTransfer.thirdPartyPayoffAmount,
      type: this.data.accountTransfer.type,
    };
  }
}
